import { Amplify } from 'aws-amplify';
import { envConfig, loadConfig } from '../../envConfig';

let configured = false;

/**
 * localStorage key recording whether the signed-in user opted out of Remember-me.
 * '1' = tokens belong in sessionStorage (clear on tab close); absent/anything else
 * = tokens belong in localStorage (Amplify's default; persists across restarts).
 *
 * Stored in *localStorage* deliberately: the preference itself must survive both
 * tab close (to know on next visit which storage to read from) and a navigation
 * to another page (where {@link configureAmplify} runs fresh).
 */
const SESSION_ONLY_KEY = 'sk_session_only';

/**
 * Re-binds Amplify's Cognito token provider to either sessionStorage (per-tab,
 * cleared on close) or the default storage (localStorage; persists). Safe to
 * call after {@link Amplify.configure}.
 */
export async function applyStoragePreference(sessionOnly: boolean): Promise<void> {
  const { cognitoUserPoolsTokenProvider } = await import('aws-amplify/auth/cognito');
  const { defaultStorage, sessionStorage: amplifySessionStorage } = await import('aws-amplify/utils');
  cognitoUserPoolsTokenProvider.setKeyValueStorage(sessionOnly ? amplifySessionStorage : defaultStorage);
}

/** Reads the persisted preference (defaults to false → use localStorage). */
function readSessionOnlyPreference(): boolean {
  try {
    return localStorage.getItem(SESSION_ONLY_KEY) === '1';
  } catch {
    return false;
  }
}

/** Persists the preference so the next page load re-applies the same storage. */
export function writeSessionOnlyPreference(sessionOnly: boolean): void {
  try {
    if (sessionOnly) localStorage.setItem(SESSION_ONLY_KEY, '1');
    else localStorage.removeItem(SESSION_ONLY_KEY);
  } catch {
    // Private mode - preference becomes session-scoped, which is a reasonable
    // fallback for "Remember me" semantics anyway.
  }
}

/**
 * Configures Amplify Auth with the Cognito user pool from envConfig. Idempotent -
 * safe to call multiple times. Awaits loadConfig() so runtime /config.json values
 * apply in production without a rebuild. Re-applies the persisted token-storage
 * preference so a user who unchecked Remember-me keeps reading tokens from
 * sessionStorage on every subsequent page.
 */
export async function configureAmplify(): Promise<void> {
  if (configured) return;
  await loadConfig();
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: envConfig.cognitoUserPoolId,
        userPoolClientId: envConfig.cognitoUserPoolClientId,
        loginWith: { email: true },
      },
    },
  });
  await applyStoragePreference(readSessionOnlyPreference());
  configured = true;
}
